import React, { PureComponent } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import {
  FormikProps, FormikValues, FormikHelpers, Formik,
} from 'formik';

import { useNavigate, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';

import {
  IProps, IState, IStateProps, IDispatchProps, ITwitterPublishSettingsViewModel,
} from './interfaces';
import {
  HeaderTitleContainer, Container, InnerContainer, Header, HeaderTitle, SettingsContainer,
  SettingsSection, Separator, SectionSeparator, ControlContainer, ControlLabel, ErrorLabel,
  ButtonContainer, FormikForm, FormButtonContainer, MaterialInputField, Selectable,
} from '../settingBaseStyles';

import { VaultButton, SocialIcon } from '../../Shared';
import {
  SettingType as PublishSettingType, IPublishSettings, ITwitterPublishSettings,
} from '../../../api/vault/publishEndpoints';
import { IAppState } from '../../../state/reducers';
import { createSettingsAction, getAllSettingsAction, updateSettingsAction } from '../../../state/publishEndpoints';

// TODO: i18n
const fieldRequiredErrorMessage = 'Field is required';
const fieldInvalidEmailMessage = 'Needs to be a valid email';

const validationSchema = Yup.object<ITwitterPublishSettingsViewModel>().shape({
  confirmationEmail: Yup.string().email(fieldInvalidEmailMessage).required(fieldRequiredErrorMessage),
  description: Yup.string(),
  profileName: Yup.string().required(fieldRequiredErrorMessage),
});

const initialSettingsState: ITwitterPublishSettingsViewModel = {
  ref: null,
  confirmationEmail: '',
  description: '',
  securityProfiles: {},
  profileName: '',
};

const createViewModel = (settings: IPublishSettings<ITwitterPublishSettings>): ITwitterPublishSettingsViewModel => ({
  ref: settings,
  confirmationEmail: settings.settings.confirmationEmail,
  description: settings.settings.description,
  securityProfiles: settings.settings.securityProfiles,
  profileName: settings.settings.profileName,
});

class TwitterPublishSettings extends PureComponent<IProps, IState> {
  createErrorLabelIfRequired = (field: string, props: FormikProps<FormikValues>) => props.errors[field] && props.touched[field] && <ErrorLabel>{props.errors[field] as string}</ErrorLabel>;

  isInProgress = (): boolean => {
    const { saveState, updateState } = this.props;
    const { isSaving } = saveState;
    const { isUpdating } = updateState;

    return isSaving || isUpdating;
  };

  formRenderer = (props: FormikProps<ITwitterPublishSettingsViewModel>): React.ReactNode => {
    const { navigate, location, securityProfiles } = this.props;

    const inProgress = this.isInProgress();

    // TODO: see if we can use material select instead of react-select (issue with material ui is that MenuItem "value" property not allowing to store objects)
    return (
      <FormikForm>
        <InnerContainer>
          <Header>
            <HeaderTitleContainer>
              <HeaderTitle>Publish</HeaderTitle>
              <SocialIcon type={PublishSettingType.Twitter} />
            </HeaderTitleContainer>
            <ButtonContainer>
              {inProgress && <CircularProgress size={24} />}
              <VaultButton type="button" disabled={inProgress} onClick={(e) => navigate(-1)}>Cancel</VaultButton>
              {Separator}
              <FormButtonContainer>
                {!location.state && <VaultButton type="button" disabled={inProgress} onClick={(e) => props.resetForm({ values: initialSettingsState })}>Reset</VaultButton>}
                <VaultButton variant="primaryOnWhite" type="submit" disabled={!props.isValid || inProgress}>Save</VaultButton>
              </FormButtonContainer>
            </ButtonContainer>
          </Header>
          <SettingsContainer>
            <SettingsSection>
              <ControlContainer>
                <ControlLabel>Profile Name *</ControlLabel>
                <MaterialInputField
                  name="profileName"
                  disabled={inProgress}
                  margin="dense"
                  variant="outlined"
                  helperText={props.touched.profileName && props.errors.profileName ? props.errors.profileName as string : ''}
                  error={props.touched.profileName && Boolean(props.errors.profileName)}
                  value={props.values.profileName}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  inputProps={{ 'aria-label': 'bare' }}
                />
              </ControlContainer>

              <ControlContainer>
                <ControlLabel>Description</ControlLabel>
                <MaterialInputField
                  name="description"
                  disabled={inProgress}
                  multiline
                  rows={8}
                  margin="dense"
                  variant="outlined"
                  helperText={props.touched.description && props.errors.description ? props.errors.description as string : ''}
                  error={props.touched.description && Boolean(props.errors.description)}
                  value={props.values.description}
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  inputProps={{ 'aria-label': 'bare' }}
                />
              </ControlContainer>
              <ControlContainer>
                <ControlLabel>Security Profiles </ControlLabel>
                <Selectable options={securityProfiles} value={props.values.securityProfiles} onChange={(e: any) => props.setFieldValue('securityProfiles', e)} />
              </ControlContainer>
            </SettingsSection>

            <SectionSeparator />

            <SettingsSection>
              {!location.state
                && (
                  <ControlContainer>
                    <ControlLabel>Confirmation Email *</ControlLabel>
                    <MaterialInputField
                      name="confirmationEmail"
                      disabled={inProgress}
                      margin="dense"
                      variant="outlined"
                      helperText={props.touched.confirmationEmail && props.errors.confirmationEmail ? props.errors.confirmationEmail as string : ''}
                      error={props.touched.confirmationEmail && Boolean(props.errors.confirmationEmail)}
                      value={props.values.confirmationEmail}
                      onChange={props.handleChange}
                      onBlur={props.handleBlur}
                      inputProps={{ 'aria-label': 'bare' }}
                    />
                  </ControlContainer>
                )}
            </SettingsSection>
          </SettingsContainer>
        </InnerContainer>
      </FormikForm>
    );
  };

  handleSubmit = (values: ITwitterPublishSettingsViewModel, formikActions: FormikHelpers<ITwitterPublishSettingsViewModel>) => {
    const {
      createSettingsActionConnect, updateSettingsActionConnect, saveState, updateState, getAllPublishSettingsConnect, navigate, location,
    } = this.props;

    if (!saveState.isSaving || !updateState.isUpdating) {
      const settings = {
        confirmationEmail: values.confirmationEmail,
        description: values.description,
        securityProfiles: values.securityProfiles,
        profileName: values.profileName,
      };
      if (location.state) {
        updateSettingsActionConnect(
          values.ref._id,
          {
            type: PublishSettingType.Twitter,
            settings,
          },
        );
      } else {
        createSettingsActionConnect({
          type: PublishSettingType.Twitter,
          settings,
        });
      }

      setTimeout(() => {
        getAllPublishSettingsConnect();
        navigate(-1);
      }, 1000);
    }
  };

  // TODO: i18n
  render() {
    const { state } = this.props.location;

    return (
      <Fade in>
        <Container>
          <Formik
            validationSchema={validationSchema}
            initialValues={state ? createViewModel(state) : initialSettingsState}
            onSubmit={this.handleSubmit}
          >
            {this.formRenderer}
          </Formik>
        </Container>
      </Fade>

    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  saveState: state.publishSettings?.saveState,
  updateState: state.publishSettings?.updateState,
  securityProfiles: state.securityProfiles?.securityProfileOptions ?? [],
});

const mapDispatchToProps: IDispatchProps = {
  createSettingsActionConnect: createSettingsAction,
  updateSettingsActionConnect: updateSettingsAction,
  getAllPublishSettingsConnect: getAllSettingsAction,
};

const TranslatedComponent = withTranslation()(TwitterPublishSettings);

const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(TranslatedComponent);

// Wrapper component to provide router hooks
const TwitterSettingsWithRouter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return <ConnectedComponent navigate={navigate} location={location} />;
};

export default TwitterSettingsWithRouter;
